import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, exhaustMap, map, throwError } from "rxjs";
import { loadCurvesActions } from "./load_curves.actions";
import { environment } from "../../../environments/environment";
import { CounterOutput, CustomerProviderCredentialsOutput, ProviderOutput } from '../../models/load_curves.model';
import { Store } from "@ngrx/store";
import { Customer, LoadCurvesCustomerFilters } from "../../models/customer.model";

@Injectable()
export class LoadCurvesEffects {
    private httpClient = inject(HttpClient);
    private actions$ = inject(Actions);
    private store = inject(Store);

    private latestCustomerId!: number;
    private latestProviderId!: number;
    private latestCounterId!: number;
    private latestCredentialsId!: number;

    $get_providers = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.getProviders),
            exhaustMap(({}) => {
                const req = this.httpClient.get<ProviderOutput[]>(`${environment.api}/load_curves/provider/all`);

                return req.pipe(
                    map((payload) => loadCurvesActions.getProvidersSuccess({data: payload})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getProvidersFail())
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    $get_customer_providers_credentials = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.getCustomerProviderCredentials),
            exhaustMap(({customer_id}) => {
                this.latestCustomerId = customer_id;
                let params = new HttpParams();
                params = params.set("customer_id", customer_id);

                const req = this.httpClient.get<CustomerProviderCredentialsOutput[]>(`${environment.api}/load_curves/credential_customer_provider/all`, {params});

                return req.pipe(
                    map((payload) => loadCurvesActions.getCustomerProviderCredentialsSuccess({data: payload})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getCustomerProviderCredentialsFail())
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    $get_customer_single_providers_credentials = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.getSingleCustomerProviderCredentials),
            exhaustMap(({credential_id}) => {
                this.latestCredentialsId = credential_id;
                const req = this.httpClient.get<CustomerProviderCredentialsOutput>(`${environment.api}/load_curves/credential_customer_provider/${credential_id}`);

                return req.pipe(
                    map((payload) => loadCurvesActions.getSingleCustomerProviderCredentialsSuccess({data: payload})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getSingleCustomerProviderCredentialsFail())
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    $get_counters = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.getCounters),
            exhaustMap(({ids}) => {
                this.latestProviderId = ids.provider_id;
                let params = new HttpParams();
                params = params.set("customer_id", ids.customer_id);
                params = params.set("provider_id", ids.provider_id);

                const req = this.httpClient.get<CounterOutput[]>(`${environment.api}/load_curves/counter/all`, {params});

                return req.pipe(
                    map((payload) => loadCurvesActions.getCountersSuccess({data: payload})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getCountersFail())
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    $get_single_counter = createEffect(() => 
        this.actions$.pipe(
            ofType(loadCurvesActions.getSingleCounter),
            exhaustMap(({counter_id}) => {
                this.latestCounterId = counter_id;
                const req = this.httpClient.get<CounterOutput>(`${environment.api}/load_curves/counter/${counter_id}`);

                return req.pipe(
                    map((payload) => loadCurvesActions.getSingleCounterSuccess({data: payload})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getSingleCounterFail())
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    $get_customers = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.getCustomers),
            exhaustMap(({data}) => {
                let params = new HttpParams();

                if(data) {
                    const filters = data as LoadCurvesCustomerFilters;

                    Object.entries(filters).forEach(([key, value]) => {
                        if(value != null) {
                            if(typeof value === "string") {
                                if(value != "") {
                                    params = params.set(key, value);
                                }
                            }
                            else {
                                params = params.set(key, value);
                            }
                        }
                    })
                }

                const req = this.httpClient.get<Customer[]>(`${environment.api}/load_curves/customer/all`, {params});

                return req.pipe(
                    map((payload) => loadCurvesActions.getCustomersSuccess({payload})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getCustomersFail())
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )


    $add_customer = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.createCustomer),
            exhaustMap(({customer_obj, user_id}) => {
                let params = new HttpParams();
                params = params.set("user_id", user_id);

                const req = this.httpClient.post(`${environment.api}/customer/${user_id}`, customer_obj);

                return req.pipe(
                    map((_) => loadCurvesActions.getCustomers({data: null})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getCustomers({data: null}))
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    $add_provider = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.createCustomerProviderCredentials),
            exhaustMap(({credential_obj}) => {
                const req = this.httpClient.post<CustomerProviderCredentialsOutput[]>(`${environment.api}/load_curves/credential_customer_provider`, credential_obj);

                return req.pipe(
                    map((payload) => loadCurvesActions.getCustomerProviderCredentials({customer_id: credential_obj.customer_id})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getCustomerProviderCredentials({customer_id: credential_obj.customer_id}))
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    $add_counter = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.createCounter),
            exhaustMap(({counter_obj}) => {
                const req = this.httpClient.post<CounterOutput>(`${environment.api}/load_curves/counter`, counter_obj);

                return req.pipe(
                    map((payload) => loadCurvesActions.getCounters({ids: {customer_id: counter_obj.customer_id, provider_id: counter_obj.provider_id}})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getCounters({ids: {customer_id: counter_obj.customer_id, provider_id: counter_obj.provider_id}}))
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )


    $edit_customer = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.editCustomer),
            exhaustMap(({customer_obj}) => {
                const req = this.httpClient.put(`${environment.api}/customer/${this.latestCustomerId}`, customer_obj);

                return req.pipe(
                    map((_) => loadCurvesActions.getCustomers({data: null})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getCustomers({data: null}))
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    $edit_provider = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.editCustomerProviderCredentials),
            exhaustMap(({credential_obj}) => {
                const req = this.httpClient.put(`${environment.api}/load_curves/credential_customer_provider/${this.latestCredentialsId}`, credential_obj);

                return req.pipe(
                    map((payload) => loadCurvesActions.getCustomerProviderCredentials({customer_id: this.latestCustomerId})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getCustomerProviderCredentials({customer_id: this.latestCustomerId}))
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )

    $edit_counter = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.editCounter),
            exhaustMap(({counter_obj}) => {
                const req = this.httpClient.put<CounterOutput>(`${environment.api}/load_curves/counter/${this.latestCounterId}`, counter_obj);

                return req.pipe(
                    map((payload) => loadCurvesActions.getCounters({ids: {customer_id: this.latestCustomerId, provider_id: this.latestProviderId}})),
                    catchError(error => {
                        this.store.dispatch(loadCurvesActions.getCounters({ids: {customer_id: this.latestCustomerId, provider_id: this.latestProviderId}}))
                        return (throwError(() => new Error(error)))
                    })
                )
            })
        )
    )


    $delete_customer = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.deleteCustomer),
            exhaustMap(({customer_id}) => {
                const req = this.httpClient.delete(`${environment.api}/customer/${customer_id}`);

                return req.pipe(
                    map((_) => loadCurvesActions.getCustomers({data: null}))
                )
            })
        )
    )

    $delete_credetials = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.deleteCustomerProviderCredentials),
            exhaustMap(({credential_id}) => {
                const req = this.httpClient.delete(`${environment.api}/load_curves/credential_customer_provider/${credential_id}`);

                return req.pipe(
                    map((_) => loadCurvesActions.getCustomerProviderCredentials({customer_id: this.latestCustomerId}))
                )
            })
        )
    )

    $delete_counter = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCurvesActions.deleteCounter),
            exhaustMap(({counter_id}) => {
                const req = this.httpClient.delete(`${environment.api}/load_curves/counter/${counter_id}`);

                return req.pipe(
                    map((_) => loadCurvesActions.getCounters({ids: {customer_id: this.latestCustomerId, provider_id: this.latestProviderId}}))
                )
            })
        )
    )
}